/**
 * Wizard for creating a new flow — extracted from FlowsPage.jsx (iter82 refactor).
 *
 * 3-step wizard:
 *   1) Pick a trigger from the catalogue (filtered by category/search)
 *   2) Name the flow
 *   3) (Optional) Register a brand-new custom trigger via the form
 *
 * Also exports the small ChipInput helper used inside the variables field.
 */
import { useEffect, useState } from "react";
import { X, Plus, Search, Sparkles, ShoppingCart, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API, auth, CATEGORY_META, CT_CATEGORIES, CT_SUBCATEGORIES } from "./shared";


export function ChipInput({ value, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  const chips = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const commit = (s) => {
    const t = String(s).trim().replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
    if (!t) return;
    if (chips.includes(t)) return setDraft("");
    onChange([...chips, t].join(", "));
    setDraft("");
  };
  const remove = (i) => onChange(chips.filter((_, j) => j !== i).join(", "));
  return (
    <div className="flex flex-wrap gap-1.5 p-2 border-2 border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 min-h-[44px] focus-within:border-[#8B1A1A]">
      {chips.map((c, i) => (
        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 text-xs font-medium" data-testid={`chip-${c}`}>
          {c}
          <button type="button" onClick={() => remove(i)} className="hover:text-red-500" aria-label={`Remove ${c}`}>
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Backspace" && !draft && chips.length) {
            remove(chips.length - 1);
          }
        }}
        onBlur={() => commit(draft)}
        placeholder={chips.length ? "Add another…" : placeholder}
        className="flex-1 min-w-[140px] outline-none bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
        data-testid={testId}
      />
    </div>
  );
}

// ───────────── New-Flow Wizard ─────────────

export function NewFlowWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1=trigger pick, 2=name, 3=custom-trigger form
  const [catalogue, setCatalogue] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Sales");
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState(null);
  const [name, setName] = useState("");

  // Custom trigger form state
  const [ct, setCt] = useState({ event: "", title: "", description: "", category: "Sales", sub_category: "Orders", variables: "" });

  const reloadCatalogue = () => fetch(`${API}/catalogue`, { headers: auth() }).then(r => r.json()).then(j => setCatalogue(j.data || []));

  useEffect(() => {
    if (!open) return;
    reloadCatalogue();
    setStep(1); setChosen(null); setName(""); setSearch("");
    setCt({ event: "", title: "", description: "", category: "Sales", sub_category: "Orders", variables: "" });
  }, [open]);

  const create = async () => {
    if (!chosen || !name.trim()) return toast.error("Pick a trigger and name your flow");
    const r = await fetch(API, {
      method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({
        name: name.trim(),
        description: chosen.description,
        is_active: false,
        trigger: { event: chosen.event, filters: chosen.sample_filters || {} },
        nodes: [{ id: `n_${Date.now()}`, type: "TRIGGER", position: { x: 100, y: 200 }, config: { event: chosen.event, filters: chosen.sample_filters || {} } }],
        edges: [],
      }),
    });
    const j = await r.json();
    if (j.success) { onCreated(j.data.flow_id); } else toast.error("Create failed");
  };

  const saveCustomTrigger = async () => {
    if (!ct.event.trim() || !ct.title.trim()) return toast.error("Event code and title are required");
    const r = await fetch(`${API}/custom-triggers`, {
      method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({
        event: ct.event.trim().toUpperCase().replace(/\s+/g, "_"),
        title: ct.title.trim(),
        description: ct.description.trim(),
        category: ct.category,
        sub_category: ct.sub_category,
        variables: ct.variables.split(",").map(s => s.trim()).filter(Boolean),
      }),
    });
    const j = await r.json();
    if (j.success) {
      toast.success(`Custom trigger ${j.data.event} created`);
      await reloadCatalogue();
      setStep(1);
      setActiveCategory(j.data.category);
    } else {
      toast.error(j.detail || j.error?.message || "Create failed");
    }
  };

  if (!open) return null;

  const filtered = catalogue.map(c => ({
    ...c,
    items: (c.items || []).filter(it =>
      !search || it.title.toLowerCase().includes(search.toLowerCase()) || (it.description || "").toLowerCase().includes(search.toLowerCase()) || it.event.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(c => (c.items || []).length > 0 || (c.sub_categories || []).some(s => s.items.length > 0));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-flow-wizard"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold dark:text-white">
              {step === 1 ? "What should trigger this flow?" : step === 2 ? "Name your flow" : "Create a custom trigger"}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {step === 1 ? "Pick an event from any area of your dashboard — or create your own." : step === 2 ? "Give it a friendly name your team will recognise." : "Define your own event your other systems can fire."}
            </p>
          </div>
          <button onClick={onClose} data-testid="close-wizard-btn"><X size={20} /></button>
        </div>

        {step === 1 && (
          <div className="flex flex-1 overflow-hidden">
            {/* Category sidebar */}
            <div className="w-52 border-r bg-slate-50 dark:bg-slate-800 p-3 overflow-y-auto">
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wide mb-2 px-1">Areas</div>
              {Object.entries(CATEGORY_META).map(([cat, meta]) => {
                const Icon = meta.icon;
                const isActive = activeCategory === cat && !search;
                const found = catalogue.find(c => c.category === cat);
                if (!found) return null;
                return (
                  <button key={cat}
                          onClick={() => { setActiveCategory(cat); setSearch(""); }}
                          data-testid={`cat-${cat.replace(/\s+/g, '-')}`}
                          className="w-full text-left mb-1.5 px-2.5 py-2 rounded-md transition flex items-center gap-2 border text-sm"
                          style={{
                            background: isActive ? meta.bg : "white",
                            color: isActive ? meta.color : "#334155",
                            borderColor: isActive ? meta.color : "#E2E8F0",
                            fontWeight: isActive ? 600 : 500,
                          }}>
                    <Icon size={14} style={{ color: meta.color }} /> {cat}
                  </button>
                );
              })}
              {/* Show Custom category if exists */}
              {catalogue.find(c => c.category === "Custom") && (
                <button onClick={() => { setActiveCategory("Custom"); setSearch(""); }}
                        data-testid="cat-Custom"
                        className="w-full text-left mb-1.5 px-2.5 py-2 rounded-md transition flex items-center gap-2 border text-sm"
                        style={{
                          background: activeCategory === "Custom" && !search ? "#FEF3C7" : "white",
                          color: activeCategory === "Custom" && !search ? "#92400E" : "#334155",
                          borderColor: activeCategory === "Custom" && !search ? "#F59E0B" : "#E2E8F0",
                          fontWeight: activeCategory === "Custom" && !search ? 600 : 500,
                        }}>
                  <Sparkles size={14} className="text-amber-500" /> Custom
                </button>
              )}
              <button
                onClick={() => setStep(3)}
                data-testid="create-custom-trigger-btn"
                className="w-full mt-4 px-2.5 py-2 rounded-md border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-200 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition"
              >
                <Plus size={14} /> New trigger
              </button>
            </div>

            {/* Trigger list */}
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="relative mb-3">
                <Search className="absolute left-2 top-2.5 text-slate-400" size={16} />
                <input
                  type="text" placeholder="Search 30+ triggers (orders, attendance, scheme…)"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]"
                  data-testid="wizard-search"
                />
              </div>
              {(search ? filtered : filtered.filter(c => c.category === activeCategory)).map(group => {
                const meta = CATEGORY_META[group.category] || { color: "#92400E", bg: "#FEF3C7", icon: Sparkles, blurb: "User-defined" };
                const subs = group.sub_categories && group.sub_categories.length > 0
                  ? group.sub_categories
                  : [{ name: "All", items: group.items }];
                return (
                  <div key={group.category} className="mb-5">
                    <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: meta.color }}>
                      <meta.icon size={12} /> {group.category} <span className="text-slate-400">— {meta.blurb}</span>
                    </div>
                    {subs.map(sub => (
                      <div key={sub.name} className="mb-3">
                        {(subs.length > 1 || sub.name !== "All") && (
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 pl-1">{sub.name}</div>
                        )}
                        <div className="space-y-2">
                          {sub.items.filter(it =>
                            !search || it.title.toLowerCase().includes(search.toLowerCase()) || (it.description || "").toLowerCase().includes(search.toLowerCase()) || it.event.toLowerCase().includes(search.toLowerCase())
                          ).map(it => (
                            <button
                              key={it.event}
                              onClick={() => { setChosen(it); setName(it.title); setStep(2); }}
                              data-testid={`pick-trigger-${it.event}`}
                              className="w-full text-left p-3 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:border-[#8B1A1A] dark:hover:border-[#8B1A1A] hover:shadow-md transition"
                            >
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-sm text-slate-900 dark:text-white">{it.title}</div>
                                {it.is_custom && <Badge variant="outline" className="text-[9px] py-0">Custom</Badge>}
                              </div>
                              <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{it.description}</div>
                              {Object.keys(it.sample_filters || {}).length > 0 && (
                                <div className="mt-1.5 text-[10px] text-slate-500 dark:text-slate-400">
                                  Defaults: {Object.entries(it.sample_filters).map(([k, v]) => `${k}=${v}`).join(", ")}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && chosen && (
          <div className="flex-1 p-6 space-y-4 overflow-y-auto">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 border-l-4 border-[#8B1A1A]">
              <div className="text-xs uppercase text-slate-500 font-semibold">Trigger</div>
              <div className="font-medium text-slate-900 dark:text-white">{chosen.title}</div>
              <div className="text-xs text-slate-500">{chosen.description}</div>
            </div>
            <div>
              <label className="text-sm font-medium dark:text-slate-300">Name your flow</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                     placeholder="e.g. Big order alert" data-testid="wizard-name-input" />
            </div>
            <div className="text-xs text-slate-500">
              You'll land on a visual canvas where you can add steps like "send a push", "wait 30 minutes", "create a task", or branch by condition. Save and activate when ready.
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="flex-1 p-6 space-y-4 overflow-y-auto" data-testid="custom-trigger-form">
            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-900 dark:text-amber-100">
              <div className="font-semibold mb-1">What is a custom trigger?</div>
              <div className="text-xs">
                A custom trigger is an event your other systems can fire to start a flow.
                For example "<b>Dealer's birthday</b>", "<b>Stock running low</b>", or "<b>Survey submitted</b>".
                After you save it here, any code or webhook can fire it via the URL shown at the bottom of this form.
              </div>
            </div>

            <div>
              <label className="text-base font-bold text-slate-900 dark:text-white block">1. Event code <span className="text-red-500">*</span></label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-1.5">A short ALL-CAPS code with underscores. Used to identify this event in code.</p>
              <Input placeholder="DEALER_BIRTHDAY"
                     value={ct.event} onChange={(e) => setCt({ ...ct, event: e.target.value })}
                     data-testid="ct-event-input"
                     className="font-mono uppercase" />
              <div className="text-[11px] text-slate-500 mt-1">Example: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">DEALER_BIRTHDAY</code></div>
            </div>
            <div>
              <label className="text-base font-bold text-slate-900 dark:text-white block">2. Friendly title <span className="text-red-500">*</span></label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-1.5">A plain-English name your team will see when picking this trigger.</p>
              <Input placeholder="When a dealer has a birthday"
                     value={ct.title} onChange={(e) => setCt({ ...ct, title: e.target.value })}
                     data-testid="ct-title-input" />
            </div>
            <div>
              <label className="text-base font-bold text-slate-900 dark:text-white block">3. Description</label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-1.5">Explain in 1–2 sentences when this event fires and why someone would use it.</p>
              <Textarea placeholder="Fires every year on the dealer's birthday at 8 AM IST so we can send a wish."
                        value={ct.description} onChange={(e) => setCt({ ...ct, description: e.target.value })}
                        rows={2} data-testid="ct-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-base font-bold text-slate-900 dark:text-white block">4. Category</label>
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-1.5">Which area of your business this belongs to.</p>
                <Select value={ct.category} onValueChange={(v) => setCt({ ...ct, category: v, sub_category: (CT_SUBCATEGORIES[v] || ["General"])[0] })}>
                  <SelectTrigger data-testid="ct-category-select" className="font-medium"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CT_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-base font-bold text-slate-900 dark:text-white block">5. Sub-category</label>
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-1.5">A more specific bucket inside the category.</p>
                <Select value={ct.sub_category} onValueChange={(v) => setCt({ ...ct, sub_category: v })}>
                  <SelectTrigger data-testid="ct-subcat-select" className="font-medium"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(CT_SUBCATEGORIES[ct.category] || ["General"]).map(sc => (
                      <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-base font-bold text-slate-900 dark:text-white block">6. Data fields <span className="text-slate-400 font-normal text-sm">(optional)</span></label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                What pieces of information will your event carry? Type each one and press <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded text-[10px]">Enter</kbd>.
                You'll be able to use them in your notifications later.
              </p>
              <ChipInput
                value={ct.variables}
                onChange={(v) => setCt({ ...ct, variables: v })}
                placeholder="Type a field name and press Enter (e.g. dealer_name, amount)"
                testId="ct-vars-input"
              />
              <div className="text-[11px] text-slate-500 mt-2">
                <span className="font-semibold">Suggestions:</span>{" "}
                {["dealer_name", "amount", "salesman_name", "date", "phone", "address"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      const cur = (ct.variables || "").split(",").map((x) => x.trim()).filter(Boolean);
                      if (!cur.includes(s)) setCt({ ...ct, variables: [...cur, s].join(", ") });
                    }}
                    className="mr-1 px-1.5 py-0.5 text-[10px] rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-sm text-emerald-900 dark:text-emerald-200">
              <div className="font-semibold mb-1">📡 How to fire this event</div>
              <div className="text-xs">
                Once saved, any system can fire your event with:
              </div>
              <pre className="mt-2 bg-white dark:bg-slate-900 border rounded p-2 text-[11px] font-mono overflow-x-auto">{`POST /api/v1/flows/_emit/${ct.event || "YOUR_EVENT"}
Body: { "data": { ${(ct.variables || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean)
  .map((v) => `"${v}": "..."`)
  .join(", ") || `"key": "value"`} } }`}</pre>
            </div>
          </div>
        )}

        <div className="flex justify-between px-6 py-3 border-t bg-slate-100 dark:bg-slate-800">
          {step !== 1 ? (
            <Button
              variant="secondary"
              onClick={() => setStep(1)}
              className="bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 font-medium"
              data-testid="wizard-back-btn"
            >
              <ChevronLeft size={14} className="mr-1" /> Back
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={onClose}
              className="bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 font-medium"
              data-testid="wizard-cancel-btn"
            >
              Cancel
            </Button>
            {step === 2 && (
              <Button onClick={create} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="create-flow-btn">
                Create & open canvas
              </Button>
            )}
            {step === 3 && (
              <Button onClick={saveCustomTrigger} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-custom-trigger-btn">
                Save trigger
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
